import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUserData from "../hooks/useUserData";
import MUIDataTable from "mui-datatables";
import { SiteStateContext } from "./SiteStateContext";



const resourceCategories = {
  "white": ["Metals", "Baobabs", "Silicon", "Space Oats", "Nuclear Waste"],
  "#40BF00": ["Petroleum", "Vis", "Jelly Beans", "Copper", "Tin", "Silver", "Titanium", "Psion Icicles"],
  "#007FFC": ["Diamond", "Quantumum", "Alien Bacteria", "Enriched Nuclear Material", "Laconia", "Plasma Crystals", "Gold"],
  "#D86D00": ["Energon", "Dark Matter", "Frozen Blob", "Adamantium", "Rubicite", "Platinum", "Ablution Crystals", "Bacta", "Fermium"],
  "#b772f8": ["Atmospheric Disturbances", "Dunes", "Roosts", "Trobbles", "Astral Worms"],
  "#992cff": ["Thickened Atmosphere", "Stabilized Weather", "Breathtaking Vistas", "Magnetic Miracle", "Biogeneticists Playground"],
  "#FE2712": ["Goop"]
};

const viewStatus = localStorage.getItem("userStatus");

if (viewStatus)
{
  console.log("View status returned as" + viewStatus);
} else

{
  console.log("No view status variable found");
}

const getResourceColor = (resourceName) => {
  if (!resourceName) return "white";
  const normalizedName = resourceName.trim().toLowerCase();
  for (const [color, resources] of Object.entries(resourceCategories)) {
    if (resources.some((res) => res.toLowerCase() === normalizedName)) {
      return color;
    }
  }
  return "white";
};

const TeamScans = () => {
  const navigate = useNavigate();
  const { userData, loading } = useUserData();
  const { impersonateTeamId, isImpersonating } = useContext(SiteStateContext);
  const isAdmin = Boolean(userData?.adminUser);
  const isAdminImpersonating = isImpersonating && isAdmin;
  const effectiveTeamId =
  isAdminImpersonating ? impersonateTeamId : userData?.teamId;
  const canAccess = Boolean(userData?.verified) || isAdminImpersonating;
  const [scans, setScans] = useState([]);
  const [rows, setRows] = useState([]);
  const [galaxySummaries, setGalaxySummaries] = useState([]);
  const [selectedGalaxies, setSelectedGalaxies] = useState(new Set());
  const [showTotalsOverlay, setShowTotalsOverlay] = useState(false);
  const [includeTeamScans, setIncludeTeamScans] = useState(true);
  const [showTeamOnly, setShowTeamOnly] = useState(false);
  const [ownersMapping, setOwnersMapping] = useState({});
  const [teamsMapping, setTeamsMapping] = useState({});

  useEffect(() => {
    if (!loading) {
      if (!userData) {
        navigate("/starsonata");
      } else if (!isAdminImpersonating && userData.teamId === "unassigned") {
        navigate("/join");
      } else if (!isAdminImpersonating && !userData.verified) {
        navigate("/pending");
      }
    }
  }, [userData, loading, navigate, isAdminImpersonating]);


  useEffect(() => {
    if (!loading && canAccess) {
      fetch("https://imp.slypandainteractive.com/galaxies").
      then((res) => res.json()).
      then(setScans).
      catch((err) => console.error("Error loading scans:", err));
    }
  }, [canAccess, loading]);


  useEffect(() => {
    fetch("/universe.json").
    then((res) => res.json()).
    then((data) => {
      const mapping = {};
      if (data.galaxies) {
        Object.values(data.galaxies).forEach((g) => {
          if (g.name && g.owningTeamID) {
            mapping[g.name.trim().toLowerCase()] = g.owningTeamID;
          }
        });
      }
      setOwnersMapping(mapping);
    }).
    catch((err) => console.error("Error loading universe.json:", err));
  }, []);


  useEffect(() => {
    fetch("/teams.json").
    then((res) => res.json()).
    then((data) => {
      const mapping = {};
      if (data.teams) {
        Object.values(data.teams).forEach((team) => {
          if (team.teamID && team.name) {
            mapping[team.teamID] = team.name;
          }
        });
      }
      setTeamsMapping(mapping);
    }).
    catch((err) => console.error("Error loading teams.json:", err));
  }, []);


  useEffect(() => {
    const grouped = {};

    scans.





    forEach((scan) => {
      if (!scan.galaxy) return;

      const galaxy = scan.galaxy.trim();
      const resource = scan.resource_name ? scan.resource_name.trim() : null;
      const color = getResourceColor(resource);
      const total = scan.total_amount;
      const team = String(scan.upload_team ?? "0");

      if (!grouped[galaxy]) grouped[galaxy] = [];

      if (resource) {
        grouped[galaxy].push({
          resource,
          total,
          color,
          upload_team: team
        });
      }
    });



    let summaries = Object.entries(grouped).
    map(([galaxy, resources]) => {
      const lowerGalaxy = galaxy.toLowerCase();
      const owningTeamId = ownersMapping[lowerGalaxy];
      const resourceStr =
      resources.length === 0 ?
      "<span style=\"opacity:0.7\">No resources</span>" :
      resources.
      map((r) => `<span style="color:${r.color}">${r.resource}: ${r.total}</span>`).
      join(", ");
      return {
        galaxy,
        owningTeamId,
        resourceStr,
        resources
      };
    }).
    sort((a, b) => a.galaxy.localeCompare(b.galaxy, undefined, { sensitivity: "base" }));

    if (showTeamOnly) {
      const myTeamId = String(effectiveTeamId ?? "");
      summaries = summaries.filter((summary) => summary.owningTeamId === myTeamId);
    }

    setGalaxySummaries(summaries);
    setRows(summaries.map(({ owningTeamId, galaxy, resourceStr }) => [owningTeamId, galaxy, resourceStr]));
  }, [scans, includeTeamScans, effectiveTeamId, ownersMapping, showTeamOnly]);

  const toggleSelection = (galaxy) => {
    setSelectedGalaxies((prev) => {
      const next = new Set(prev);
      if (next.has(galaxy)) {
        next.delete(galaxy);
      } else {
        next.add(galaxy);
      }
      return next;
    });
  };

  const handleSelectAllVisible = () => {
    const visibleGalaxies = rows.map(([, galaxy]) => galaxy);
    const visibleSet = new Set(visibleGalaxies);
    const allVisibleSelected =
    visibleGalaxies.length > 0 &&
    visibleGalaxies.every((galaxy) => selectedGalaxies.has(galaxy));

    if (allVisibleSelected) {
      setSelectedGalaxies((prev) => {
        const next = new Set(prev);
        visibleSet.forEach((galaxy) => next.delete(galaxy));
        return next;
      });
    } else {
      setSelectedGalaxies((prev) => {
        const next = new Set(prev);
        visibleSet.forEach((galaxy) => next.add(galaxy));
        return next;
      });
    }
  };

  const allVisibleSelected =
  rows.length > 0 && rows.every(([, galaxy]) => selectedGalaxies.has(galaxy));

  const tierOrder = ["white", "#40BF00", "#007FFC", "#D86D00", "#b772f8", "#992cff", "#FE2712"];

  const aggregatedResourceTiers = useMemo(() => {
    const totals = new Map();
    galaxySummaries.forEach((summary) => {
      if (!selectedGalaxies.has(summary.galaxy)) return;
      summary.resources.forEach((resource) => {
        const amount = Number(resource.total) || 0;
        totals.set(resource.resource, (totals.get(resource.resource) || 0) + amount);
      });
    });

    const tierBuckets = tierOrder.map((color) => ({
      color,
      resources: []
    }));

    Array.from(totals.entries()).forEach(([resource, total]) => {
      const color = getResourceColor(resource);
      const tierIndex = tierOrder.indexOf(color);
      const bucket = tierIndex >= 0 ? tierBuckets[tierIndex] : tierBuckets[0];
      bucket.resources.push({ resource, total, color });
    });

    return tierBuckets.map((bucket) => ({
      ...bucket,
      resources: bucket.resources.sort((a, b) =>
      a.resource.localeCompare(b.resource, undefined, { sensitivity: "base" })
      )
    }));
  }, [selectedGalaxies, galaxySummaries]);

  useEffect(() => {
    if (selectedGalaxies.size === 0) {
      setShowTotalsOverlay(false);
    }
  }, [selectedGalaxies]);



  const columns = [
  {
    name: "Owner",
    options: {
      sort: false,
      filter: false,
      setCellHeaderProps: () => ({ style: { textAlign: "center", whiteSpace: "nowrap" } }),
      setCellProps: () => ({ style: { textAlign: "center", whiteSpace: "nowrap" } }),
      customBodyRender: (teamId) =>
      teamId && teamId !== "0" ?
      <img
        src={`https://www.starsonata.com/images/team_flags/game/${teamId}`}
        alt={`Team ${teamId}`}
        title={teamsMapping[teamId] || `Team ${teamId}`}
        style={{ maxWidth: "40px", maxHeight: "40px" }} /> :


      "—"

    }
  },
  {
    name: "Galaxy",
    options: {
      setCellHeaderProps: () => ({ style: { textAlign: "center", whiteSpace: "nowrap" } }),
      setCellProps: () => ({ style: { textAlign: "center", whiteSpace: "nowrap" } })
    }
  },
  {
    name: "Resources",
    options: {
      setCellHeaderProps: () => ({ style: { textAlign: "center", whiteSpace: "nowrap" } }),
      customBodyRender: (value) =>
      <span dangerouslySetInnerHTML={{ __html: value }} />

    }
  }];



  const options = {
    selectableRows: "none",
    elevation: 0,
    responsive: "standard",
    rowsPerPage: 100,
    onRowClick: (rowData) => {
      const galaxy = rowData[1];
      navigate(`/scandetails/${encodeURIComponent(galaxy)}`);
    },
    componentsProps: {
      tableHeadCell: {
        className: "custom-table-header"
      }
    },
    setRowProps: () => ({
      className: "custom-table-cell"
    }),
    setTableProps: () => ({
      className: "custom-table-root"
    }),
    textLabels: {
      body: {
        noMatch: "No scans found."
      }
    }
  };



  return (
    <div style={{ padding: "0 1rem" }}>
      <div
        style={{
          border: "1px solid #444",
          borderRadius: "10px",
          padding: "1.5rem",
          backgroundColor: "#1e1e1e",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          width: "100%",
          maxWidth: "1320px",
          margin: "0 auto",
          boxSizing: "border-box"
        }}>
        
      <h3 style={{ textAlign: "center", margin: "0 0 0.75rem" }}>
        Galaxy Scans
      </h3>
      <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "0.75rem",
            marginBottom: "0.75rem"
          }}>
          
        {showTeamOnly ?
          <button
            type="button"
            className="back-button"
            onClick={() => setShowTeamOnly(false)}>
            
            Show All
          </button> :

          <button
            type="button"
            className="back-button"
            onClick={() => setShowTeamOnly(true)}>
            
            Show Team
          </button>
          }
      </div>
      <div
          style={{
            padding: "1rem 1.5rem 1.5rem",
            borderRadius: "6px",
            backgroundColor: "#1e1e1e",
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem"
          }}>
          
        {false &&
          <div className="scans-table-wrap">
            <MUIDataTable
              title={null}
              data={rows}
              columns={columns}
              options={{
                ...options,
                setRowProps: () => ({
                  className: "custom-table-cell"
                }),
                setTableProps: () => ({
                  className: "custom-table-root"
                })
              }} />
            
          </div>
          }
        <div className="team-scan-table">
        <div className="team-scan-table__header">
          <div className="team-scan-table__cell team-scan-table__cell--owner">Owner</div>
          <div className="team-scan-table__cell">Galaxy</div>
          <div className="team-scan-table__cell">Resources</div>
          <div className="team-scan-table__cell team-scan-table__cell--select">
            <input
                  className="team-scan-table__checkbox"
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={handleSelectAllVisible}
                  title="Select all visible"
                  aria-label="Select all visible" />
                
          </div>
        </div>
          {rows.length === 0 ?
            <div className="team-scan-table__empty">No scans found.</div> :

            rows.map(([owningTeamId, galaxy, resourceStr]) => {
              const teamName =
              owningTeamId && teamsMapping[owningTeamId] ?
              teamsMapping[owningTeamId] :
              `Team ${owningTeamId}`;
              return (
                <div
                  key={galaxy}
                  className="team-scan-table__row"
                  onClick={() => navigate(`/scandetails/${encodeURIComponent(galaxy)}`)}>
                  
                  <div className="team-scan-table__cell team-scan-table__cell--owner">
                    {owningTeamId && owningTeamId !== "0" ?
                    <img
                      src={`https://www.starsonata.com/images/team_flags/game/${owningTeamId}`}
                      alt={teamName}
                      title={teamName}
                      style={{ maxWidth: "40px", maxHeight: "40px" }} /> :


                    "N/A"
                    }
                  </div>
                  <div className="team-scan-table__cell">{galaxy}</div>
                  <div
                    className="team-scan-table__cell team-scan-table__cell--resources"
                    dangerouslySetInnerHTML={{ __html: resourceStr }} />
                  
                  <div className="team-scan-table__cell team-scan-table__cell--select">
                    <input
                      className="team-scan-table__checkbox"
                      type="checkbox"
                      checked={selectedGalaxies.has(galaxy)}
                      onChange={() => toggleSelection(galaxy)}
                      onClick={(e) => e.stopPropagation()} />
                    
                  </div>
                </div>);

            })
            }
        </div>
      </div>
      </div>
      {selectedGalaxies.size > 0 &&
      <div
        className={`scan-combined-overlay${
        showTotalsOverlay ? " scan-combined-overlay--expanded" : ""}`
        }
        role="dialog"
        aria-modal="true">
        
          <div className="scan-combined-overlay__panel">
            <button
            className="back-button scan-combined-overlay__toggle"
            onClick={() => setShowTotalsOverlay((prev) => !prev)}>
            
              {showTotalsOverlay ? "Hide Breakdown" : "Show Breakdown"} (
              {selectedGalaxies.size})
            </button>
            {showTotalsOverlay &&
          <>
                <div className="scan-combined-overlay__header">
                  <h3>Combined Totals</h3>
                </div>
                <div className="scan-combined-summary">
                  <div className="scan-combined-summary__rows">
                    {aggregatedResourceTiers.map(
                  (tier) =>
                  tier.resources.length > 0 &&
                  <div key={tier.color} className="scan-combined-summary__row">
                        <div className="scan-combined-summary__chips scan-combined-summary__chips--row">
                              {tier.resources.map((entry, index) =>
                      <span key={entry.resource}>
                                  <span
                          className="scan-combined-summary__chip"
                          style={{ color: entry.color }}>
                          
                                    {entry.resource}: {entry.total}
                                  </span>
                                  {index < tier.resources.length - 1 ?
                        <span style={{ color: tier.color }}> | </span> :

                        ""
                        }
                                </span>
                      )}
                            </div>
                          </div>

                )}
                  </div>
                </div>
              </>
          }
          </div>
        </div>
      }
    </div>);

};

export default TeamScans;